import {useOverlayActivity} from "components/useOverlayActivity";
import React, {useRef, useState} from "react";
import {Dialog, useTheme} from "@mui/material";
import BaseInput from "components/BaseInput";
import {FeedbackTopicSelect} from "components/FeedbackTopicSelect";
import {useMessages} from "i18n";
import {overlay, shadows} from "theme/tokens";
import {SheetTransition} from "transitions/SheetTransition";
import {useIsomorphicLayoutEffect} from "utils/useIsomorphicLayoutEffect";
import {useSupportRequest} from "./useSupportRequest";
import {
	MAX_SUPPORT_SUBJECT_LENGTH,
	type SupportFieldErrorKey,
	supportFieldErrorText,
} from "./supportRequestValidation";
import {
	Actions,
	Body,
	CloseButton,
	Eyebrow,
	FailureBlock,
	FailureText,
	Field,
	FieldError,
	Fields,
	Form,
	GhostButton,
	Head,
	HeadInner,
	Lede,
	MailtoLink,
	MessageCounter,
	ReplyToNote,
	Sheet,
	SlaNote,
	Spinner,
	SubmitButton,
	Success,
	SuccessMark,
	SuccessNote,
	SuccessTitle,
	Title,
	TitleAccent,
} from "./SupportRequestDialog.styles";

/** Stable ids, so each field can point at its own counter and error through `aria-describedby`. */
const TOPIC_FIELD_ID = "support-topic";
const SUBJECT_FIELD_ID = "support-subject";
const SUBJECT_ERROR_ID = "support-subject-error";
const MESSAGE_FIELD_ID = "support-message";
const MESSAGE_COUNTER_ID = "support-message-counter";
const MESSAGE_ERROR_ID = "support-message-error";

/** Joins the description ids a field actually has; an id that describes nothing is worse than none. */
function describedBy(...ids: (string | false)[]): string | undefined {
	const present = ids.filter((id): id is string => id !== false);
	return present.length > 0 ? present.join(" ") : undefined;
}

interface SupportRequestDialogProps {
	/**
	 * Custom trigger. Receives an `open` callback so a caller can render any clickable element (today
	 * the Resources menu's Support row, which since ui#520 is the app's only one) while reusing this
	 * exact dialog.
	 */
	trigger: (open: () => void) => React.ReactNode;
	/** Replaces MUI's implicit restore target when a custom trigger disappears while the dialog is open. */
	onTransitionExited?: () => void;
}

/**
 * The authenticated support-request dialog (kitecorp/transferify-api-service#101) — a dumb,
 * restyleable view over the {@link useSupportRequest} controller (epic #26). All copy comes from the
 * `support` i18n namespace; all colors from `theme.tokens`.
 *
 * The PRIORITY framing is the only thing that varies, and it varies by ENTITLEMENT, not by dialog:
 * a plan carrying the `prioritySupport` capability sees the surface named "Priority support" and gets
 * the SLA promise; everyone else fills in the identical form with no promise attached. There is
 * deliberately no second form, no reduced field set and no upsell for lower tiers — support is not the
 * place to sell, and a shared form means a fix to it reaches every tier at once.
 *
 * Distinct from `feedback/Feedback.tsx`, which remains the PUBLIC contact form posting to `/feedback`
 * (api-service#217) for logged-out visitors.
 */
export default function SupportRequestDialog({trigger, onTransitionExited}: SupportRequestDialogProps) {
	const m = useMessages();
	const {tokens} = useTheme();
	const [open, setOpen] = useState(false);
	const observedExit = useOverlayActivity(open, onTransitionExited);
	const support = useSupportRequest();
	const sendButton = useRef<HTMLButtonElement>(null);

	// Keyboard focus must stay inside the dialog for the whole send (kitecorp/transferify-ui#3076). The
	// fields are disabled while a send is in flight, and a disabled element cannot hold focus: the
	// browser moves it to the page body, where Escape reaches nothing until MUI's focus trap pulls it
	// back on its next 50 ms tick. The Send button is the one control that stays focusable throughout
	// (see its `aria-disabled` below), so a send started from the subject field with Enter, or by a
	// click that did not focus the button (Safari's default), moves focus there. A layout effect, so the
	// move happens in the same task as the commit that disabled the field, before any key can arrive.
	useIsomorphicLayoutEffect(() => {
		if (support.submitting) {
			sendButton.current?.focus();
		}
	}, [support.submitting]);

	const handleOpen = () => setOpen(true);
	const handleClose = () => {
		setOpen(false);
		// Clear the success/failure moment so the next open starts from a fresh form.
		support.reset();
	};

	// A real submit, so Enter in either field sends. `noValidate` because the browser's own bubble
	// cannot be translated or placed at the field — one owned, server-faithful error path beats two
	// disagreeing ones.
	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		void support.submit();
	};

	// The length quoted by a "too long" complaint is the budget the CHOSEN TOPIC leaves, not the raw
	// api bound — the field's own `maxLength` stops typing at that same number.
	const fieldErrorText = (key: SupportFieldErrorKey | null) =>
		key === null ? null : supportFieldErrorText(m.support.fieldErrors, key, support.maxMessageLength);
	const subjectError = fieldErrorText(support.fieldErrors.subject);
	const messageError = fieldErrorText(support.fieldErrors.message);

	const dialog = (
		<Dialog
			open={open}
			onClose={handleClose}
			disableRestoreFocus={onTransitionExited !== undefined}
			maxWidth={false}
			slots={{transition: SheetTransition}}
			slotProps={{
				backdrop: {style: {background: overlay.scrim}},
				// `onExited` on the TRANSITION, never `onTransitionExited` on the Dialog: MUI runs the
				// latter BEFORE `ModalManager.remove` un-hides the background, so a restore wired there
				// aims at a still-`aria-hidden` page. See the same note in {@link components/ModalSheet}.
				transition: {onExited: observedExit},
				paper: {
					"aria-label": support.priority ? m.support.ariaLabelPriority : m.support.ariaLabel,
					style: {
						width: "460px",
						maxWidth: "calc(100% - 32px)",
						maxHeight: "calc(100% - 32px)",
						margin: 0,
						borderRadius: 24,
						backgroundColor: tokens.card,
						backgroundImage: "none",
						border: `1px solid ${tokens.cardBorder}`,
						boxShadow: shadows.modal,
						overflow: "hidden",
					},
				},
			}}
		>
			<Sheet>
				<CloseButton type="button" onClick={handleClose} aria-label={m.common.close}>✕</CloseButton>

				{support.submitted ? (
					<Body>
						<Success>
							<SuccessMark aria-hidden="true">✓</SuccessMark>
							<SuccessTitle>{m.support.success.title}</SuccessTitle>
							<SuccessNote>{m.support.success.note}</SuccessNote>
							{/* The promise is repeated where the user is actually waiting on it. */}
							{support.priority && <SuccessNote>{m.support.sla}</SuccessNote>}
							{/* This view replaces the form, and the focused Send button with it, so focus
							    moves here as it mounts instead of dropping to the page (ui#3076). */}
							<SubmitButton type="button" onClick={handleClose} autoFocus>{m.common.done}</SubmitButton>
						</Success>
					</Body>
				) : (
					<>
						<Head>
							<HeadInner>
								<Eyebrow>{support.priority ? m.support.eyebrowPriority : m.support.eyebrow}</Eyebrow>
								<Title>
									{m.support.title} <TitleAccent>{m.support.titleAccent}</TitleAccent>
								</Title>
								<Lede>{m.support.lede}</Lede>
								{support.priority && <SlaNote>{m.support.sla}</SlaNote>}
							</HeadInner>
						</Head>

						<Body>
							<Form onSubmit={handleSubmit} noValidate>
								<Fields>
									{/* First, because it frames the fields under it: the user says what the request
									    is about before writing it. Optional — see FeedbackTopicSelect. */}
									<Field>
										<FeedbackTopicSelect
											id={TOPIC_FIELD_ID}
											value={support.topic}
											onChange={support.setTopic}
											disabled={support.submitting}
										/>
									</Field>

									<Field>
										<BaseInput
											id={SUBJECT_FIELD_ID}
											label={m.support.subjectLabel}
											value={support.subject}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => support.setSubject(e.target.value)}
											placeholder={m.support.subjectPlaceholder}
											disabled={support.submitting}
											error={subjectError !== null}
											aria-describedby={describedBy(subjectError !== null && SUBJECT_ERROR_ID)}
											// Stops the user AT the api's bound instead of letting them compose a
											// subject that comes back as a 400 (`@Size(max = 200)`).
											slotProps={{input: {maxLength: MAX_SUPPORT_SUBJECT_LENGTH}}}
										/>
										{subjectError && <FieldError id={SUBJECT_ERROR_ID} role="alert">{subjectError}</FieldError>}
									</Field>

									<Field>
										<BaseInput
											id={MESSAGE_FIELD_ID}
											label={m.support.messageLabel}
											value={support.message}
											onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => support.setMessage(e.target.value)}
											placeholder={m.support.messagePlaceholder}
											disabled={support.submitting}
											error={messageError !== null}
											aria-describedby={describedBy(MESSAGE_COUNTER_ID, messageError !== null && MESSAGE_ERROR_ID)}
											multiline
											minRows={5}
											maxRows={9}
											// The api's bound MINUS the chosen topic's inbox tag, because the tag is
											// part of the body the api measures.
											slotProps={{input: {maxLength: support.maxMessageLength}}}
										/>
										<MessageCounter
											id={MESSAGE_COUNTER_ID}
											$atLimit={support.message.length >= support.maxMessageLength}
											aria-label={m.support.messageCounterLabel(support.maxMessageLength)}
										>
											{m.support.messageCounter(support.message.length, support.maxMessageLength)}
										</MessageCounter>
										{messageError && <FieldError id={MESSAGE_ERROR_ID} role="alert">{messageError}</FieldError>}
									</Field>
								</Fields>

								{/* Where the answer goes. Rendered only once the account address is actually known:
								    an empty address would be a promise pointing nowhere, and the address is absent
								    by design during the pre-render and the first client render. */}
								{support.replyToAddress !== "" && (
									<ReplyToNote>{m.support.replyToNotice(support.replyToAddress)}</ReplyToNote>
								)}

								{/* One block per outcome, in the dialog rather than a toast — the undeliverable case
								    carries the address the user must fall back to, which a toast cannot hold. The
								    sentence and the address both come from the controller: it prefers the api's own
								    explanation over our copy, and the address follows the storefront (ui#2261). */}
								{support.failure && (
									<FailureBlock role="alert">
										<FailureText>{support.failureText}</FailureText>
										{support.showMailtoFallback && (
											<MailtoLink href={`mailto:${support.supportAddress}`}>
												{support.supportAddress}
											</MailtoLink>
										)}
									</FailureBlock>
								)}

								<Actions>
									<GhostButton type="button" onClick={handleClose}>{m.common.cancel}</GhostButton>
									{/* DISABLED only for an incomplete draft, which also stops Enter from submitting
								    it. While sending it is `aria-disabled` instead: still focusable, so focus
								    never leaves the dialog, and a press joins the send already in flight. */}
								<SubmitButton
									ref={sendButton}
									type="submit"
									disabled={!support.draftComplete}
									aria-disabled={support.submitting || undefined}
								>
										{support.submitting
											? <><Spinner aria-hidden="true"/>{m.support.submitting}</>
											: m.support.submit}
									</SubmitButton>
								</Actions>
							</Form>
						</Body>
					</>
				)}
			</Sheet>
		</Dialog>
	);

	return <>{dialog}{trigger(handleOpen)}</>;
}
